/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Modal,
  SvgIcon,
  Button,
  Heading,
  Table,
  Checkbox,
  List,
  SpanWithData
} from "../"

import {
  getSldsIconType,
  showAsCurrency,
  isEmptyObject,
  flattenArray
} from '../../utils'

export class DeductionUploadConfirm extends React.Component {
  static propTypes = {
    file: PropTypes.string.isRequired,
    changes: PropTypes.object.isRequired,  // acceptable changes in uploaded file
    prevented: PropTypes.arrayOf(PropTypes.string).isRequired,  // changes prevented
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,  // errors in changes proposed
    ambiguous: PropTypes.arrayOf(PropTypes.string).isRequired,  // ambiguous changes after fuzzy matching
    onCancel: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      paymentsToSave: this.initialiseChangedPaymentsToSave()
    }
  }

  initialiseChangedPaymentsToSave = () => {
    let changes = this.props.changes
    let changedPayments = []
    let paymentsToSave = {}

    for (let id in changes) {
      // for every change record, add its payments array to changedPayments
      // - more than one payment can be associated with a single change record
      changedPayments = [ ...changedPayments, ...changes[id].payments ]
    }

    changedPayments.forEach(changedPayment => {
      paymentsToSave[changedPayment.payment] = changedPayment
    })

    return paymentsToSave
  }

  handlePaymentSelect = (change) => {
    let paymentsToSave = Object.assign({}, this.state.paymentsToSave)
    if (this.state.paymentsToSave.hasOwnProperty(change.payment)) {
      // if payment id already in paymentsToSave object, it was unselected and therefore remove
      delete paymentsToSave[change.payment]
    } else {
      // if payment id not in paymentsToSave object, it was selected and therefore add
      paymentsToSave[change.payment] = change
    }
    this.setState({ paymentsToSave })
  }

  savePayment = (changedPayment) => {
    let input = {
      id: changedPayment.payment,  // 'payment' field holds payment id
      statusReason: 'Unknown - uploader change'
    }

    if (changedPayment.change === "Removed") {
      input.amount = 0
      input.state = "changed"
    } else if (changedPayment.change.slice(0,3) === "Was") {
      input.amount = changedPayment.amount
      input.state = "changed"
    }
    this.props.savePayment({ variables: { input } })
    .then(({ data }) => (
      console.log(`Changed payment (${data.paymentMutate.payment.id}) saved.`))
    )
    .catch((error) => (
      console.log(`Attempt to save changed payment (${input.id}) failed.`, error)
    ))
  }

  saveChangedPayments = () => {
    let changedPaymentsToSave = []
    let paymentsToSave = this.state.paymentsToSave
    for (let paymentId in paymentsToSave) {
      changedPaymentsToSave.push(this.savePayment(paymentsToSave[paymentId]))
    }

    Promise.all(changedPaymentsToSave)
    .then(() => {
      console.log("Changed payments all saved successfully")
      this.props.onSaved()
    })
    .catch(() => console.log("An error occurred while saving changed payments"))
  }

  render() {
    let content = <div className="slds-grid slds-wrap">
      <div className="slds-size_1-of-1">
        <DeductionUploadConfirmHeader
          filename={this.props.file}
          onCancel={this.props.onCancel}
          onAcceptSelected={this.saveChangedPayments}
          acceptSelectedDisabled={isEmptyObject(this.props.changes)}
        />
      </div>
      <DeductionChanges
        changes={this.props.changes}
        onSelectAll={() => this.setState({
          paymentsToSave: this.initialiseChangedPaymentsToSave()
        })}
        onPaymentSelect={this.handlePaymentSelect}
      />
      {this.props.prevented && this.props.prevented.length > 0
        ? <DeductionPrevented prevented={this.props.prevented} />
        : <span></span>
      }
      {this.props.errors && this.props.errors.length > 0
        ? <DeductionErrors errors={this.props.errors} />
        : <span></span>
      }
      {this.props.ambiguous && this.props.ambiguous.length > 0
        ? <DeductionAmbiguous ambiguous={this.props.ambiguous} />
        : <span></span>
      }
    </div>

    return (
      <Modal
        body={content}
        sizeLarge={true}
      />
    )
  }
}

const DeductionUploadConfirmHeader = (props) => (
  <div className="slds-grid">
    <div>
      <SvgIcon
        variant="doctype"
        symbol={getSldsIconType("xls")}
      />
    </div>
    <div className="slds-m-top_x-small slds-m-left_small">
      <b>Upload:</b>
      <span className="slds-m-left_x-small">
        {props.filename}
      </span>
    </div>
    <div className="slds-col_bump-left">
      <Button
        className="u-text-caps"
        onClick={props.onCancel}>
        Cancel
      </Button>
      <Button
        className="u-text-caps"
        type="brand"
        disabled={props.acceptSelectedDisabled}
        onClick={props.onAcceptSelected}>
        Accept selected
      </Button>
    </div>
  </div>
)

class DeductionChanges extends React.Component {
  static propTypes = {
    changes: PropTypes.object.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    onPaymentSelect: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      selectAll: true
    }
  }

  uncheckSelectAll = () => {
    this.setState({ selectAll: false })
  }

  getChangesRows = (changes) => (
    changes.map((change, index) => {
      let changeIndex = index
      return change.map((row, rowIndex) => (
        <DeductionChangesTableRow
          data={row}
          selectAll={this.state.selectAll}
          uncheckSelectAll={this.uncheckSelectAll}
          onSelect={this.props.onPaymentSelect}
          key={`deductionChangesRow_${changeIndex}-${rowIndex}`}
        />
      ))
    })
  )

  render() {
    if (!this.props.changes || Object.keys(this.props.changes).length === 0) {
      return <div className="slds-size_1-of-1 slds-m-top_large">
        <Heading marginBottom={5}>
          <b>0 Changes</b>
        </Heading>
        <p className="slds-text-heading_small">
          No changes to apply
        </p>
      </div>
    } else {
      let changesObj = this.props.changes
      let changesArray = Object.keys(changesObj)
      let changes = changesArray.map((identity) => (
        changesObj[identity].payments.map((payment) => (
          {
            firstName: changesObj[identity].first_name,
            lastName: changesObj[identity].last_name,
            identityValue: changesObj[identity].identity_value,
            employeeNumber: changesObj[identity].employee_number,
            payment: payment.payment,
            product: payment.product,
            paidBy: payment.source === "client" ? "Self" : "Employer",
            amount: payment.amount,
            change: payment.change
          }
        ))
      ))
      let numberOfChanges = flattenArray(changes).length
      let tableRows = this.getChangesRows(changes)

      return (
        <div className="slds-size_1-of-1 slds-m-top_large">
          <Heading marginBottom={5}>
            <b>{`${numberOfChanges} Changes`}</b>
          </Heading>
          <p className="slds-text-heading_small">
            Please review the changes below and if you&rsquo;re happy, accept the changes.
            You can uncheck (<span className="slds-m-left_xx-small slds-m-right_xx-small"
              style={{ display: "inline-block"}}> <Checkbox id="example" checked={true} onClick={() => false} />
            </span>) items you want to exclude. If you want to make other changes,
            please make them on your original spreadsheet and re-upload your new file.
          </p>
          <Table
            columns={[<Checkbox
              id="selectAll"
              checked={this.state.selectAll}
              onChange={() => this.setState((prevState) => {
                let selectAllNew = !prevState.selectAll
                if (selectAllNew) {
                  this.props.onSelectAll()
                }
                return {
                  selectAll: selectAllNew
                }
              })}
            />, "Name", "Surname", "ID", "Product", "Paid by", "Amount", "Change"]}
            rows={tableRows}
            objectRows={true}
            noRowBorders={true}
            className="slds-max-medium-table_stacked-horizontal slds-no-row-hover"
          />
        </div>
      )
    }
  }
}

class DeductionPrevented extends React.Component {
  static propTypes = {
    prevented: PropTypes.arrayOf(PropTypes.string).isRequired
  }

  render() {
    let numberOfPreventableErrors = this.props.prevented.length

    return (
      <div className="slds-size_1-of-1 slds-m-top_large">
        <Heading marginBottom={5}>
          <b>{`${numberOfPreventableErrors} Unchanged`}</b>
        </Heading>
        <p className="slds-text-heading_small">
          Items were left unchanged for the following reasons:
        </p>
        <List
          items={this.props.prevented}
          type="decimal"
          border="bottom"
          className="slds-m-left_medium"
        />
      </div>
    )
  }
}

class DeductionErrors extends React.Component {
  static propTypes = {
    errors: PropTypes.arrayOf(PropTypes.string).isRequired
  }

  render() {
    let numberOfErrors = this.props.errors.length

    return (
      <div className="slds-size_1-of-1 slds-m-top_large">
        <Heading marginBottom={5}>
          <b>{`${numberOfErrors} Ignored`}</b>
        </Heading>
        <p className="slds-text-heading_small">
          The following were ignored because they appear to be mistakes:
        </p>
        <List
          items={this.props.errors}
          type="decimal"
          border="bottom"
          className="slds-m-left_medium"
        />
      </div>
    )
  }
}

class DeductionAmbiguous extends React.Component {
  static propTypes = {
    ambiguous: PropTypes.arrayOf(PropTypes.string).isRequired
  }

  render() {
    let numberOfErrors = this.props.ambiguous.length

    return (
      <div className="slds-size_1-of-1 slds-m-top_large">
        <Heading marginBottom={5}>
          <b>{`${numberOfErrors} ignored`}</b>
        </Heading>
        <p className="slds-text-heading_small">
          The following payments were unable to be accurately and unambiguously
          matched without a reference number. Please manually change them by finding
          the affected client and updating them.
        </p>
        <List
          items={this.props.ambiguous}
          type="decimal"
          border="bottom"
          className="slds-m-left_medium"
        />
      </div>
    )
  }
}

class DeductionChangesTableRow extends React.Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    selectAll: PropTypes.bool,
    uncheckSelectAll: PropTypes.func,
    onSelect: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      selected: this.props.selectAll
    }
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.selectAll && !this.state.selected) {
      this.setState({
        selected: true
      })
    }
  }

  selectRow = (data) => {
    if (this.props.selectAll) {  // if 'select all' checkbox checked, uncheck on select of a child row
      this.props.uncheckSelectAll()
    }
    this.props.onSelect(data)
    this.setState((prevState) => ({ selected: !prevState.selected }))
  }

  render() {
    let data = this.props.data
    let changeTypeRowClass = data.change === "Removed"
        ? "u-bg-color-lightcoral__alpha"
        : ''

    let amountChangeClass = data.change.slice(0,3) === "Was"
      ? "u-bg-color-lightgreen__alpha"
      : ''

    return (
      <tr className={`${true ? "slds-border_top " : ''}${changeTypeRowClass}`} >
        <td>
          <Checkbox
            checked={this.state.selected}
            onChange={() => this.selectRow(data)}
          />
        </td>
        <td data-label="Name">
          {data.firstName}
        </td>
        <td data-label="Surname">
          {data.lastName}
        </td>
        <td data-label="ID">
          {data.identityValue}
        </td>
        <td data-label="Product">
          {data.product}
        </td>
        <td data-label="Paid by">
          {data.paidBy}
        </td>
        <td data-label="Amount" className={amountChangeClass}>
          {showAsCurrency('R', data.amount)}
        </td>
        <td data-label="State">
          {data.change}
        </td>
      </tr>
    )
  }
}

const savePayment = gql`
  mutation savePayment($input: PaymentMutationInput!) {
    paymentMutate(input: $input) {
      payment {
        id
        amount
        state
        workflowrunDeducting {
          id
        }
      }
    }
  }
`

const withPaymentMutate =
  graphql(savePayment, {
    name: 'savePayment'
  })

export default withPaymentMutate(DeductionUploadConfirm)



// WEBPACK FOOTER //
// ./src/components/deduction-upload-confirm/index.js